import moment from "moment-timezone";
// ⚠ Upward import, deliberately. This is the package's ONE viewer-zone
// resolver and `formatTimeRange` labels online session times with it, so a row
// that resolved its own would drift from the picker that sold it — which is
// the whole failure this module exists to prevent. `format.ts` imports nothing
// from `lib/`, so there is no cycle.
import { resolveViewerTimezone } from "../../components/checkout/utils/format";

/**
 * The time window a booking actually occupies, formatted for display.
 *
 * Rentals and appointments both carry `appointmentData`, which is the only
 * place an END time exists on a basket item — `sessionDate` is a single
 * instant. Before this, four surfaces each formatted the start on their own
 * (the shared basket card, the webcomponents Basket and its checkout overlay,
 * and the widget's checkout page) and not one of them showed the end or the
 * duration, so a customer could not tell a 1.5-hour hire from a full day.
 *
 * ⚠ `timezone` is the LISTING's, not the browser's, and it is required for the
 * same reason `wallClockInstant` takes one: a customer booking a London
 * listing from Sydney must see London's clock, or the window they read is not
 * the window they bought. Callers that genuinely have no timezone should pass
 * the school default rather than letting `moment` fall back to system local.
 */

export type BookingWindowSource = {
  /** Rentals and appointments. The only source that carries an end. */
  appointmentData?: {
    startTime?: string | null;
    endTime?: string | null;
    duration?: number | null;
  } | null;
  /** Everything else: a single instant, so no window can be derived. */
  sessionDate?: string | null;
  /** ISO instant, when the item carries an explicit end. */
  sessionEndDate?: string | null;
};

/**
 * "3h", "1h 30m", "45m" — the hours a customer is buying.
 *
 * Whole hours drop the minutes entirely: "3h", not "3h 0m".
 *
 * ⚠ The long-form sibling is `formatRentalDuration` in `@obby/lib`
 * (`rental/startOptions.ts`), which renders the same minutes as "1 hour 30
 * minutes" for the confirmation email and the partner booking page. The two
 * always agree on the VALUE and differ only in presentation, deliberately:
 * this one is appended inside a line that already carries a date and a window
 * ("Thu 18 Jun, 09:30–11:00 (1h 30m)"), where the long form does not fit.
 * Keep them in step — if the arithmetic here ever changes, change it there.
 */
export function formatBookingDuration(minutes: number): string | null {
  if (!Number.isFinite(minutes) || minutes <= 0) return null;
  // Round BEFORE splitting. Flooring 119.7 gives 1 hour and rounding its
  // remainder gives 60 — "1h 60m".
  const total = Math.round(minutes);
  if (total <= 0) return null;
  const whole = Math.floor(total / 60);
  const rest = total % 60;
  if (whole === 0) return `${rest}m`;
  if (rest === 0) return `${whole}h`;
  return `${whole}h ${rest}m`;
}

/**
 * Whether moment-timezone actually holds data for this zone.
 *
 * ⚠ Validating a zone against `Intl.DateTimeFormat` is NOT enough for anything
 * this module then formats. `Intl` throws `RangeError` on a name it does not
 * know, so a try/catch around it looks like validation — but `moment.tz`
 * throws nothing: it logs "Moment Timezone has no data for …" and silently
 * formats in UTC. The two disagree for any name the browser's ICU knows and
 * the bundled tz data does not (newer tzdata additions), which puts a day on
 * the listing's clock beside a window in UTC.
 */
export function isKnownTimezone(zone?: string | null): zone is string {
  return !!zone && moment.tz.zone(zone) != null;
}

/**
 * The zone a row should be rendered in: the first candidate moment can format
 * in, and failing all of them the BROWSER's.
 *
 * ⚠ Never returns undefined, deliberately. `school.timezone` is a free-form
 * String with no enum, so "GMT+1" or "BST" are storable, and callers that
 * dropped the window when nothing validated left every row on the last screen
 * before payment showing a bare date — losing information the customer needs
 * to confirm, for a whole school at once. The browser's zone is not the
 * listing's, but it is honest as long as the DAY beside it is rendered in the
 * same one, which is why this is resolved once per row and used for both.
 */
export function resolveDisplayTimezone(
  ...zones: Array<string | null | undefined>
): string {
  const named = zones.find(isKnownTimezone);
  if (named) return named;
  // ⚠ `resolveViewerTimezone()` first, NOT `moment.tz.guess()`. They disagree
  // by construction: the former hands back the browser's IANA name verbatim —
  // the same one `formatTimeRange` labels online times with — while `guess()`
  // substitutes an offset-matched zone whenever the bundled tzdata lacks that
  // name, which is the ICU-known/moment-unknown case this file already guards
  // elsewhere. `guess()` remains the last resort: it always returns something
  // formattable, which `resolveViewerTimezone()` does not (it is undefined
  // outside a browser).
  const viewer = resolveViewerTimezone();
  return isKnownTimezone(viewer) ? viewer : moment.tz.guess();
}

/**
 * The END of a window, qualified with its day when that is not the start's.
 *
 * ⚠ A rental window crossing midnight is ordinary, not an edge case:
 * `startOptions` rolls an opening window past midnight on purpose
 * (`if (window.endMin <= window.startMin) window.endMin += 24 * 60`) and a
 * hire's length is unbounded above. Callers prefix the START's day — "Fri 19
 * Jun, 22:00–02:00" — so a bare end time asserts a return that same night,
 * and a 24-hour day rate rendered "09:00–09:00", which reads as no window at
 * all.
 *
 * The comparison is made in the LISTING's zone, because both moments are
 * built in it: 23:00–01:00 in New York is one local evening even though it
 * spans two UTC days.
 */
export function formatWindowEnd(
  start: moment.Moment,
  end: moment.Moment,
): string {
  return start.isSame(end, "day")
    ? end.format("HH:mm")
    : end.format("ddd D MMM HH:mm");
}

/**
 * "09:30–12:30", or "22:00 – Sat 20 Jun 02:00" when it crosses midnight.
 *
 * ⚠ `startLabel` exists for callers whose picker prints the start from raw
 * minutes. On a spring-forward day a start inside the missing hour has no wall
 * clock and moment resolves it an hour on, so formatting it from the moment
 * would print "02:30" beneath a button the customer tapped reading "01:30".
 * The END has no such alternative — it is a real instant either way.
 */
export function formatWindowRange(
  start: moment.Moment,
  end: moment.Moment,
  startLabel: string = start.format("HH:mm"),
): string {
  const endLabel = formatWindowEnd(start, end);
  // Spaced only when the end carries a date: "22:00–Sat 20 Jun 02:00" runs the
  // two together, while a same-day range is tighter without them.
  return start.isSame(end, "day")
    ? `${startLabel}–${endLabel}`
    : `${startLabel} – ${endLabel}`;
}

/**
 * "09:30–12:30 (3h)", or "09:30" when no end is known.
 *
 * Parenthesised rather than separated by a middot: the dot read as stray
 * punctuation next to a date, and it matches the rental flow's own
 * "Duration (3h)" label.
 *
 * 24-hour throughout, per the project's time-format rule — the widget's
 * checkout page rendered `h:mm A` before this.
 *
 * Returns null when there is no start at all, so callers can omit the line
 * rather than render an empty clock.
 */
export function formatBookingWindow(params: {
  item: BookingWindowSource;
  timezone: string;
}): string | null {
  const { item, timezone } = params;

  // Start and end are taken as a PAIR from one source. Resolving them through
  // independent fallback chains let an item with appointmentData.startTime but
  // no endTime borrow `sessionEndDate`, pairing an appointment's start with a
  // session's end. No producer emits that shape today; the pairing makes it
  // impossible rather than merely unlikely.
  const fromAppointment = item.appointmentData?.startTime
    ? {
        start: item.appointmentData.startTime,
        end: item.appointmentData.endTime || null,
        duration: item.appointmentData.duration ?? null,
      }
    : null;
  const source =
    fromAppointment ??
    (item.sessionDate
      ? { start: item.sessionDate, end: item.sessionEndDate || null, duration: null }
      : null);

  const startIso = source?.start ?? null;
  if (!startIso) return null;
  const start = moment.tz(startIso, timezone);
  if (!start.isValid()) return null;

  const endIso = source?.end ?? null;
  const end = endIso ? moment.tz(endIso, timezone) : null;

  // Prefer the stored duration over end−start: it is what the customer was
  // charged for.
  //
  // ⚠ The two cannot currently disagree, DST included. Every producer builds
  // `endTime` as `start.clone().add(minutes)`, which is absolute-time
  // arithmetic, so the subtraction returns the stored figure exactly. An
  // earlier version of this comment claimed a 3h hire across the autumn change
  // subtracts to 4h; it does not, and it would take a producer doing
  // wall-clock arithmetic to make it so. The preference is kept because the
  // stored duration is the CHARGED length, so it should win if an end ever
  // arrives from another source.
  const minutes =
    typeof source?.duration === "number" && source.duration > 0
      ? source.duration
      : end && end.isValid()
        ? end.diff(start, "minutes")
        : null;

  const durationLabel = minutes == null ? null : formatBookingDuration(minutes);

  if (!end || !end.isValid()) {
    return durationLabel
      ? `${start.format("HH:mm")} (${durationLabel})`
      : start.format("HH:mm");
  }

  const window = formatWindowRange(start, end);
  return durationLabel ? `${window} (${durationLabel})` : window;
}

/** A basket row, as the two drawers see it. */
export type BasketRowWindowSource = BookingWindowSource & {
  timezone?: string | null;
  bookingType?: string | null;
  isOnline?: boolean | null;
  /** Present on a term row, which renders a start–end DATE range. */
  termData?: unknown;
};

/**
 * Whether a row must be rendered on the LISTING's clock — which is true of
 * every row that shows more than a bare start.
 *
 * ⚠ `termData` counts even though it is not a window. Those are calendar
 * DATES, not instants: a `startDate` stored at midnight UTC formatted west of
 * the listing names the previous day, so "1 Sep" becomes "31 Aug". Both
 * checkout screens document that branch as listing-clock, and it is currently
 * unreachable — no producer writes `termData` — but the invariant should not
 * quietly depend on that.
 */
function rowCarriesAnEnd(item: BasketRowWindowSource): boolean {
  return (
    !!item?.appointmentData ||
    !!item?.sessionEndDate ||
    !!item?.termData ||
    item?.bookingType === "appointment"
  );
}

/**
 * The clock ONE basket row should be rendered on: whichever one the picker
 * that sold it used.
 *
 * ⚠ That split is NOT "online vs in-person" — it is which flow produced the
 * row, and the two do not line up:
 *
 * - The SESSION flows (regular, term, drop-in, trial) label their times with
 *   `formatTimeRange`, which takes `isOnline` and switches to the viewer's
 *   clock, because an online class has no venue and the buyer's own clock is
 *   the meaningful one. Their rows follow suit.
 * - The APPOINTMENT, RENTAL and CALENDAR-VISIBILITY flows do not. Their
 *   pickers are hard-coded to the listing's zone (`checkout-appointment.tsx`'s
 *   `timezone || course.timezone || "Europe/London"`, and the same in
 *   `checkout-rental`), and they render their grids from listing wall-clock
 *   minutes — while still stamping `isOnline: course.isOnline` on the item.
 *   Sending those rows to the viewer's clock made an online consultation read
 *   09:30 in the picker and 04:30 in the basket.
 *
 * Carrying an end is exactly what separates the two groups, which is why it is
 * the condition here: a row with a window came from a listing-clock picker.
 * If those pickers ever learn the online rule, this comes with them.
 */
export function resolveRowTimezone(
  item: BasketRowWindowSource,
  ...fallbacks: Array<string | null | undefined>
): string {
  if (item?.isOnline && !rowCarriesAnEnd(item)) return resolveDisplayTimezone();
  return resolveDisplayTimezone(item?.timezone, ...fallbacks);
}

/**
 * The window a BASKET DRAWER row should show, or undefined for none.
 *
 * Shared because the two drawers must agree, and duplicating the rule across
 * two apps is how they would stop agreeing — one gains a clause, the other
 * does not, and the same basket reads differently in the widget and in the
 * webcomponents build. That is the disagreement this module exists to end.
 *
 * ⚠ The item's OWN zone, and only when it is usable. A drawer has no school
 * zone to fall back on — a widget basket can hold items from several schools,
 * so the container's would be the wrong clock for some rows. An item cached
 * before this shipped carries none at all. Both cases show the date alone,
 * while the final screens, which DO have a school zone, show the full window:
 * a missing time is recoverable, a wrong one is not.
 *
 * ⚠ Gated on the DATA, not the zone. Every flow stamps a timezone now, so
 * using that as the gate would have started showing a bare start time on
 * regular/term/drop-in/trial rows — a change to the most common booking type
 * that this work deliberately does not make. Rentals and main-flow
 * appointments carry `appointmentData`; a calendar-visibility booking is an
 * appointment without one and keeps its bare start; a bundle redemption
 * carries `sessionEndDate`, a real end the final screen already renders from.
 */
export function formatBasketRowWindow(
  item: BasketRowWindowSource,
): string | undefined {
  // Always the listing's zone: a row with a window came from a picker that
  // renders in it, online or not — see `resolveRowTimezone`.
  if (!isKnownTimezone(item.timezone)) return undefined;
  if (!rowCarriesAnEnd(item)) return undefined;
  return formatBookingWindow({ item, timezone: item.timezone }) ?? undefined;
}
